import { FormControl, FormField } from '@/components/ui/Form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Form, FormItem, FormLabel } from '@/components/wm';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CHART_PERIODS, DEFAULT_RATE_BY_PERIOD, recommendedChartType, RESOLUTIONS } from '../utils';
import { savedIndicatorSite, SiteIndicatorFields } from './SiteIndicatorFields';
import { Widget } from './Widget';

// Period labels: the rolling windows are specific to charts, the to-date ones are
// the same the rest of the app uses.
const periodLabel = period => (
    ['wtd', 'mtd', 'ytd'].includes(period)
        ? i18next.t(`periods.verbose.${period}`).capitalize()
        : i18next.t(`dashboard.custom_dashboard.periods.${period}`)
);

const resolutionLabels = {
    900: 'periods.15m',
    3600: 'periods.1h',
    86400: 'periods.1d',
    2592000: 'periods.1m',
    31536000: 'periods.1y'
};

export function NewIndicatorChartData({ data, onChangeField, onCompleted }) {
    const
        decimalOptions = ['0', '1', '2', '3'],
        unitScaleOptions = [
            { label: i18next.t('unit_scales.auto'), value: -1 },
            { label: i18next.t('unit_scales.full'), value: 0 },
            { label: i18next.t('unit_scales.thousands'), value: 3 },
            { label: i18next.t('unit_scales.millions'), value: 6 },
            { label: i18next.t('unit_scales.billions'), value: 9 }
        ];

    // Radix hands back strings, so the numeric fields are coerced rather than
    // declared as z.number() — which would reject '3' and silently block Next.
    const formSchema = z.object({
        decimals: z.string().min(1, i18next.t('form_validations.generic.required')),
        indicatorId: z.string().min(1, i18next.t('form_validations.generic.indicator_required')),
        period: z.string().min(1, i18next.t('form_validations.generic.required')),
        // Positive, not just an int: the empty default coerces to 0, which would
        // otherwise pass and let a chart through with no resolution.
        rate: z.coerce.number().int().positive(i18next.t('form_validations.generic.required')),
        site: z.string().min(1, i18next.t('form_validations.generic.required')),
        unitScale: z.coerce.number().int(i18next.t('form_validations.generic.required'))
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            decimals: data.data?.decimals || '2',
            indicatorId: data.data?.indicatorId || '',
            period: data.data?.period || '',
            rate: data.data?.rate || '',
            site: savedIndicatorSite(data.data?.indicatorId),
            unitScale: data.data?.unitScale ?? -1
        },
        mode: 'onBlur'
    });

    const debouncedTriggers = useRef({
        decimals: debounce(() => form.trigger('decimals'), 600),
        period: debounce(() => form.trigger('period'), 600),
        rate: debounce(() => form.trigger('rate'), 600),
        unitScale: debounce(() => form.trigger('unitScale'), 600)
    });

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    // The general step offers the chart type that suits the new data, picked.
    const preselectChartType = changes => onChangeField('graphical')({
        ...data.graphical,
        chartType: recommendedChartType(changes)
    });

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form {...form}>
                    <SiteIndicatorFields
                        data={data}
                        emptyLabel={i18next.t('indicators.no_indicators_found')}
                        filter={() => true}
                        form={form}
                        onChangeField={onChangeField}
                        onIndicatorChange={(_, indicatorId) => onChangeField('data')({ ...data.data, indicatorId })}
                    />
                    <FormField
                        control={form.control}
                        name="period"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('common.reference_period').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            // Re-bucket to something sensible for the new window: an
                                            // hourly bucket over 12 months would be thousands of bars.
                                            const rate = DEFAULT_RATE_BY_PERIOD[val];

                                            form.setValue('rate', rate);

                                            onChangeField('data')({
                                                ...data.data,
                                                period: val,
                                                rate
                                            });
                                            preselectChartType({ period: val, rate });

                                            debouncedTriggers.current.period();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('dashboard.custom_dashboard.select_a_period')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {CHART_PERIODS.map(period => (
                                                <SelectItem key={period} value={period}>
                                                    {periodLabel(period)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="rate"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('common.resolution').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(Number(val));

                                            onChangeField('data')({
                                                ...data.data,
                                                rate: Number(val)
                                            });
                                            preselectChartType({ period: data.data?.period, rate: Number(val) });

                                            debouncedTriggers.current.rate();
                                        }}
                                        value={String(field.value)}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('dashboard.custom_dashboard.select_a_resolution')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {RESOLUTIONS.map(resolution => (
                                                <SelectItem key={resolution} value={String(resolution)}>
                                                    {i18next.t(resolutionLabels[resolution])}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="unitScale"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('unit_scales.auto').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(Number(val));

                                            onChangeField('data')({
                                                ...data.data,
                                                unitScale: Number(val)
                                            });

                                            debouncedTriggers.current.unitScale();
                                        }}
                                        value={String(field.value)}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('unit_scales.auto')}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {unitScaleOptions.map(unitScale => (
                                                <SelectItem key={unitScale.value} value={String(unitScale.value)}>
                                                    {unitScale.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="decimals"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{i18next.t('glossary.decimals').capitalize()}</FormLabel>
                                <FormControl>
                                    <Select
                                        onValueChange={val => {
                                            field.onChange(val);

                                            onChangeField('data')({
                                                ...data.data,
                                                decimals: val
                                            });

                                            debouncedTriggers.current.decimals();
                                        }}
                                        value={field.value}
                                    >
                                        <SelectTrigger className="tw-ml-0">
                                            <SelectValue placeholder={i18next.t('glossary.decimals').capitalize()}/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {decimalOptions.map(decimal => (
                                                <SelectItem key={decimal} value={decimal}>
                                                    {decimal}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </Form>
            </div>
            <div className="tw-w-[320px] tw-h-[200px] tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
